import { useEffect, useRef } from 'react';

import {
  JoystickActionConfig,
  JoystickConfigMap,
  JoystickInputs,
  useGamepadContext,
} from '@/providers';
import { TOGGLE_FOCUSED_CAMERA_SELECTOR_EVENT } from '@/providers/gamepadProvider/gamepadProvider.constants.ts';
import {
  confirmCameraSelector,
  navigateCameraSelector,
} from '@/providers/gamepadProvider/gamepadProvider.help.ts';
import { CAMERA_STREAM_WIDGET } from '@/widgetComponentNames.ts';

type UseGlobalCameraStreamWidgetGamepadParams = {
  widgetId: string;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
};
export const useGlobalCameraStreamWidgetGamepad = ({
  widgetId,
  isModalOpen,
  openModal,
  closeModal,
}: UseGlobalCameraStreamWidgetGamepadParams) => {
  const { setGlobalMap, clearGlobalMap } = useGamepadContext();
  const modalOpenedAtRef = useRef(0);

  useEffect(() => {
    const handleToggleCameraSelectorModal = () => {
      if (isModalOpen) {
        closeModal();
        return;
      }
      if (!isGlobalCameraWidgetFocused(widgetId)) return;
      openModal();
    };
    window.addEventListener(TOGGLE_FOCUSED_CAMERA_SELECTOR_EVENT, handleToggleCameraSelectorModal);
    return () => {
      window.removeEventListener(
        TOGGLE_FOCUSED_CAMERA_SELECTOR_EVENT,
        handleToggleCameraSelectorModal
      );
    };
  }, [isModalOpen, closeModal, openModal, widgetId]);

  useEffect(() => {
    if (isModalOpen) {
      modalOpenedAtRef.current = performance.now();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    const cameraSelectorInputMap: JoystickConfigMap = {
      [JoystickInputs.LeftStick.Move]: {
        handler: args => {
          if (!args) return;
          const absX = Math.abs(args.x);
          const absY = Math.abs(args.y);
          if (absX < 0.55 && absY < 0.55) return;
          if (absX >= absY) {
            navigateCameraSelector(args.x > 0 ? 'right' : 'left');
            return;
          }
          navigateCameraSelector(args.y > 0 ? 'down' : 'up');
        },
        repeat: true,
        intervalMs: 170,
      } satisfies JoystickActionConfig,
      [JoystickInputs.DPad.Up]: {
        handler: () => navigateCameraSelector('up'),
        repeat: true,
        intervalMs: 170,
      } satisfies JoystickActionConfig,
      [JoystickInputs.DPad.Down]: {
        handler: () => navigateCameraSelector('down'),
        repeat: true,
        intervalMs: 170,
      } satisfies JoystickActionConfig,
      [JoystickInputs.DPad.Left]: {
        handler: () => navigateCameraSelector('left'),
        repeat: true,
        intervalMs: 170,
      } satisfies JoystickActionConfig,
      [JoystickInputs.DPad.Right]: {
        handler: () => navigateCameraSelector('right'),
        repeat: true,
        intervalMs: 170,
      } satisfies JoystickActionConfig,
      [JoystickInputs.Face.A]: {
        handler: () => confirmCameraSelector(),
        repeat: false,
      } satisfies JoystickActionConfig,
      [JoystickInputs.LeftStick.Press]: {
        handler: () => {
          // Ignore the same physical press used to open the modal.
          if (performance.now() - modalOpenedAtRef.current < 280) return;
          closeModal();
        },
        repeat: false,
      } satisfies JoystickActionConfig,
    };
    setGlobalMap(cameraSelectorInputMap);
    return () => {
      clearGlobalMap();
    };
  }, [isModalOpen, setGlobalMap, clearGlobalMap, closeModal]);
};

const isGlobalCameraWidgetFocused = (widgetId: string) => {
  const activeElement = document.activeElement as HTMLElement | null;
  if (!activeElement) return false;
  const cameraWidgetRoot = activeElement.closest<HTMLElement>(
    `[data-component="${CAMERA_STREAM_WIDGET}"]`
  );
  if (!cameraWidgetRoot) return false;
  const gridItem = cameraWidgetRoot.closest<HTMLElement>('.grid-stack-item');
  return gridItem?.getAttribute('gs-id') === widgetId;
};
